import { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Search, Plus, ChevronRight, Trash2, Pencil, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useConfirm } from '@/components/ConfirmDialog';
import { SearchableSelect } from '@/components/SearchableSelect';
import { pillStyle } from '@/components/StatusDot';
import { expirationColor } from '@/lib/permitDate';
import { LinkExistingDialog } from '@/components/LinkExistingDialog';
import { inProject, buildProjectLinkPatch } from '@/lib/projectLink';

const STATUS_FILTERS = ['Active', 'Expiring Soon', 'Expired'];
// Computed-status pill colors: green (Active), amber (Expiring Soon), red (Expired).
const STATUS_COLORS = { 'Active': '#16A34A', 'Expiring Soon': '#D97706', 'Expired': '#E5441A' };

const EMPTY_FORM = { property_id: '', project_id: '', label: '', expiration_date: '', notes: '' };

const StatusPill = ({ status }) => {
  if (!status) return <span style={{ color: '#010A21' }}>{'\u00A0'}</span>;
  return <span style={pillStyle(STATUS_COLORS[status] || '#71717A')} data-testid="dot-batch-status-pill">{status}</span>;
};

// Simplified DOT permit BATCH tracking (one expiration date for a batch of permits).
// scope: lockedProjectId (Project view) > lockedPropertyId (Property view) > hub (all).
export default function DotBatchTrackers({ lockedPropertyId, lockedProjectId }) {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [trackers, setTrackers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState('All');
  const [search, setSearch] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_FORM);

  const [linkOpen, setLinkOpen] = useState(false);
  const [linking, setLinking] = useState(false);
  const [linkCandidates, setLinkCandidates] = useState([]);

  const showPropertyCol = !lockedPropertyId && !lockedProjectId;

  const fetchTrackers = () => {
    const params = {};
    if (lockedProjectId) params.project_id = lockedProjectId;
    else if (lockedPropertyId) params.property_id = lockedPropertyId;
    return api.get('/dot-batch-trackers', { params }).then(r => setTrackers(Array.isArray(r.data) ? r.data : [])).catch(() => setTrackers([]));
  };

  useEffect(() => {
    setLoading(true);
    fetchTrackers().finally(() => setLoading(false));
    api.get('/properties').then(r => setProperties(Array.isArray(r.data) ? r.data : [])).catch(() => setProperties([]));
    api.get('/projects').then(r => setProjects(Array.isArray(r.data) ? r.data : [])).catch(() => setProjects([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedPropertyId, lockedProjectId]);

  const lockedProp = (Array.isArray(properties) ? properties : []).find(p => p.id === lockedPropertyId);
  const defaultPropertyId = lockedPropertyId || '';

  const openAdd = () => {
    setAddForm({ ...EMPTY_FORM, property_id: defaultPropertyId, project_id: lockedProjectId || '' });
    setAddOpen(true);
  };

  // Fetch this property's batch trackers not already on this project, then open the picker.
  const openLink = async () => {
    setLinkOpen(true);
    try {
      const r = await api.get('/dot-batch-trackers', { params: { property_id: lockedPropertyId } });
      const all = Array.isArray(r.data) ? r.data : [];
      setLinkCandidates(all.filter(t => !inProject(t, lockedProjectId)));
    } catch { setLinkCandidates([]); }
  };

  const linkSelected = async (ids) => {
    setLinking(true);
    try {
      await Promise.all(ids.map(id => {
        const t = linkCandidates.find(x => x.id === id);
        return api.put(`/dot-batch-trackers/${id}`, buildProjectLinkPatch(t, lockedProjectId));
      }));
      toast.success(`Linked ${ids.length} tracker${ids.length === 1 ? '' : 's'} to this project`);
      setLinkOpen(false);
      fetchTrackers();
    } catch (e) { toast.error('Could not link some trackers'); }
    setLinking(false);
  };

  const createTracker = async () => {
    if (!addForm.property_id) return toast.error('Property is required');
    if (!addForm.label.trim()) return toast.error('Label is required');
    if (!addForm.expiration_date) return toast.error('Expiration date is required');
    setSaving(true);
    try {
      await api.post('/dot-batch-trackers', { ...addForm, project_id: addForm.project_id || null });
      toast.success('Batch tracker added');
      setAddOpen(false);
      setAddForm(EMPTY_FORM);
      fetchTrackers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error adding batch tracker'); }
    setSaving(false);
  };

  const toggleRow = (t) => {
    if (expandedId === t.id) { setExpandedId(null); setDetail(null); setEditMode(false); return; }
    setExpandedId(t.id);
    setDetail(t);
    setEditMode(false);
  };

  const startEdit = () => {
    setEditForm({
      property_id: detail.property_id || '', project_id: detail.project_id || '',
      label: detail.label || '', expiration_date: (detail.expiration_date || '').slice(0, 10),
      notes: detail.notes || '',
    });
    setEditMode(true);
  };

  const saveEdit = async () => {
    if (!editForm.label.trim()) return toast.error('Label is required');
    if (!editForm.expiration_date) return toast.error('Expiration date is required');
    setSaving(true);
    try {
      const r = await api.put(`/dot-batch-trackers/${detail.id}`, { ...editForm, project_id: editForm.project_id || null });
      setDetail(r.data);
      setTrackers(prev => prev.map(x => (x.id === r.data.id ? r.data : x)));
      setEditMode(false);
      toast.success('Batch tracker updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error updating batch tracker'); }
    setSaving(false);
  };

  const deleteTracker = async (id) => {
    const ok = await confirm({ title: 'Delete batch tracker', description: 'Delete this batch tracker? This cannot be undone.', confirmLabel: 'Delete', destructive: true });
    if (!ok) return;
    try {
      await api.delete(`/dot-batch-trackers/${id}`);
      toast.success('Batch tracker deleted');
      setExpandedId(null);
      setDetail(null);
      fetchTrackers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error deleting batch tracker'); }
  };

  const filtered = (Array.isArray(trackers) ? trackers : []).filter(t => {
    if (statusFilter !== 'All' && t.status !== statusFilter) return false;
    const q = search.toLowerCase();
    if (q && !(
      (t.label || '').toLowerCase().includes(q) ||
      (t.property_display_name || '').toLowerCase().includes(q) ||
      (t.property_address || '').toLowerCase().includes(q)
    )) return false;
    return true;
  });

  const renderFields = (form, setForm, prefix) => {
    const propertyOpts = (Array.isArray(properties) ? properties : []).map(p => ({ value: p.id, label: `${p.display_name || p.address}${p.borough ? `: ${p.borough}` : ''}` }));
    const projectOpts = (Array.isArray(projects) ? projects : []).filter(p => form.property_id && p.property_id === form.property_id).map(p => ({ value: p.id, label: `${p.project_number}: ${p.name}` }));
    return (
      <div className="space-y-3">
        {lockedPropertyId ? (
          <div>
            <Label className="text-xs text-muted-foreground">Property</Label>
            <Input value={lockedProp ? (lockedProp.display_name || lockedProp.address) : 'This property'} disabled className="bg-secondary/40 border-border" data-testid={`${prefix}-property-locked`} />
          </div>
        ) : (
          <div>
            <Label className="text-xs text-muted-foreground">Property *</Label>
            <SearchableSelect options={propertyOpts} value={form.property_id} onChange={(val) => setForm({ ...form, property_id: val, project_id: '' })} placeholder="Select property" searchPlaceholder="Search properties..." emptyText="No properties found." allowClear={false} testid={`${prefix}-property`} />
          </div>
        )}
        {!lockedProjectId && (
          <div>
            <Label className="text-xs text-muted-foreground">Project <span className="text-muted-foreground/60">(optional)</span></Label>
            <SearchableSelect options={projectOpts} value={form.project_id} onChange={(val) => setForm({ ...form, project_id: val })} placeholder={form.property_id ? 'Link a project (optional)' : 'Select a property first'} searchPlaceholder="Search projects..." emptyText={form.property_id ? 'No projects linked to this property.' : 'Select a property first.'} testid={`${prefix}-project`} />
          </div>
        )}
        <div>
          <Label className="text-xs text-muted-foreground">Label *</Label>
          <Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder='e.g. DOT Work Permits, Broadway Storefront' className="bg-background border-border" data-testid={`${prefix}-label`} />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Expiration Date *</Label>
          <Input type="date" value={form.expiration_date || ''} onChange={e => setForm({ ...form, expiration_date: e.target.value })} className="bg-background border-border" data-testid={`${prefix}-expiration-date`} />
          <p className="text-[11px] text-muted-foreground mt-1">Status is computed automatically: green over 30 days out, amber within 30 days, red once past.</p>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Notes</Label>
          <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="bg-background border-border" data-testid={`${prefix}-notes`} />
        </div>
      </div>
    );
  };

  const renderDetailPanel = () => {
    if (!detail) return null;
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          {!editMode && <Button size="sm" variant="outline" className="border-border" onClick={startEdit} data-testid="edit-dot-batch-btn"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>}
          <Button size="sm" variant="outline" className="text-destructive hover:text-destructive border-border" onClick={() => deleteTracker(detail.id)} data-testid="delete-dot-batch-btn"><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
        </div>
        {editMode ? (
          <>
            {renderFields(editForm, setEditForm, 'edit-dot-batch')}
            <div className="flex gap-2 mt-2">
              <Button onClick={saveEdit} disabled={saving} className="bg-primary text-primary-foreground" data-testid="save-dot-batch-btn">{saving ? 'Saving...' : 'Save Changes'}</Button>
              <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
            </div>
          </>
        ) : (
          <div className="space-y-4" data-testid="dot-batch-detail-view">
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              <p className="text-sm">
                <span style={{ color: '#031333' }}>Label: </span>
                <span style={{ color: '#010A21' }}>{detail.label || '\u00A0'}</span>
              </p>
              <p className="text-sm">
                <span style={{ color: '#031333' }}>Status: </span>
                <StatusPill status={detail.status} />
              </p>
              <p className="text-sm">
                <span style={{ color: '#031333' }}>Expiration Date: </span>
                <span style={{ fontFamily: "'DM Mono', monospace", color: expirationColor(detail.expiration_date) || '#010A21' }}>{detail.expiration_date || '\u00A0'}</span>
              </p>
              {showPropertyCol && (
                <p className="text-sm">
                  <span style={{ color: '#031333' }}>Property: </span>
                  {detail.property_id
                    ? <button onClick={() => navigate(`/properties/${detail.property_id}`)} className="text-[#1B4A76] hover:underline" data-testid="dot-batch-detail-property-link">{detail.property_display_name || detail.property_address || 'View property'}</button>
                    : <span style={{ color: '#010A21' }}>-</span>}
                </p>
              )}
              <p className="text-sm">
                <span style={{ color: '#031333' }}>Project: </span>
                {detail.project_id
                  ? <button onClick={() => navigate(`/projects/${detail.project_id}`)} className="text-[#1B4A76] hover:underline" data-testid="dot-batch-detail-project-link">View project</button>
                  : <span style={{ color: '#010A21' }}>-</span>}
              </p>
            </div>
            {detail.notes && (
              <div>
                <p className="text-sm" style={{ color: '#031333' }}>Notes:</p>
                <p className="text-sm whitespace-pre-wrap" style={{ color: '#010A21' }}>{detail.notes}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const colCount = showPropertyCol ? 4 : 3;

  return (
    <div className="space-y-4" data-testid="dot-batch-trackers-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm text-muted-foreground" data-testid="dot-batch-summary">
            {filtered.filter(t => t.status === 'Active').length} active batch tracker(s)
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">Simplified tracking for a batch of DOT permits sharing one expiration date.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button className="bg-primary text-primary-foreground" onClick={openAdd} data-testid="add-dot-batch-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Batch Tracker
          </Button>
          {lockedProjectId && lockedPropertyId && (
            <Button variant="outline" className="border-border" onClick={openLink} data-testid="link-existing-dot-batch-btn">
              <Link2 className="h-4 w-4 mr-1" /> Link Existing
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44 bg-card border-border text-sm" data-testid="dot-batch-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="All" className="text-sm">All Statuses</SelectItem>
            {STATUS_FILTERS.map(s => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search batch trackers..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border" data-testid="dot-batch-search" />
        </div>
      </div>

      <div className="bg-transparent">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs text-muted-foreground">Label</TableHead>
              {showPropertyCol && <TableHead className="text-xs text-muted-foreground">Property</TableHead>}
              <TableHead className="text-xs text-muted-foreground">Status</TableHead>
              <TableHead className="text-xs text-muted-foreground">Expiration Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(t => (
              <Fragment key={t.id}>
                <TableRow className={`group border-border cursor-pointer ${expandedId === t.id ? 'bg-secondary/40' : 'hover:bg-secondary/30'}`} onClick={() => toggleRow(t)} data-testid={`dot-batch-row-${t.id}`} style={{ height: '40px' }}>
                  <TableCell className="text-sm">
                    <span className="inline-flex items-center gap-1.5">
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground transition-transform" style={{ transform: expandedId === t.id ? 'rotate(90deg)' : 'none' }} />
                      {t.label}
                    </span>
                  </TableCell>
                  {showPropertyCol && (
                    <TableCell className="text-sm">
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/properties/${t.property_id}`); }} className="text-[#1B4A76] hover:underline text-left" data-testid={`dot-batch-property-link-${t.id}`}>
                        {t.property_display_name || t.property_address || '\u00A0'}
                      </button>
                    </TableCell>
                  )}
                  <TableCell><StatusPill status={t.status} /></TableCell>
                  <TableCell className="text-sm" style={{ fontFamily: "'DM Mono', monospace", color: expirationColor(t.expiration_date) }} data-testid={`dot-batch-exp-${t.id}`}>{t.expiration_date || '\u00A0'}</TableCell>
                </TableRow>
                <tr data-testid={`dot-batch-expand-${t.id}`}>
                  <td colSpan={colCount} style={{ padding: 0, border: 0 }}>
                    <div style={{ maxHeight: expandedId === t.id ? '2000px' : '0', overflow: 'hidden', transition: 'max-height 200ms ease-out' }}>
                      {expandedId === t.id && (
                        <div className="px-4 py-4 bg-secondary/20 border-b border-border" data-testid={`dot-batch-detail-${t.id}`}>
                          {renderDetailPanel()}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              </Fragment>
            ))}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={colCount} className="text-center text-sm text-muted-foreground py-8">No batch trackers found. Click "Add Batch Tracker" to record one.</TableCell></TableRow>
            )}
            {loading && (
              <TableRow><TableCell colSpan={colCount} className="text-center text-sm text-muted-foreground py-8">Loading batch trackers...</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Sheet open={addOpen} onOpenChange={setAddOpen}>
        <SheetContent className="sm:max-w-md bg-card border-border overflow-y-auto" data-testid="add-dot-batch-sheet">
          <SheetHeader>
            <SheetTitle>Add DOT Batch Tracker</SheetTitle>
            <SheetDescription>Track a batch of DOT permits that share one expiration date.</SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            {renderFields(addForm, setAddForm, 'add-dot-batch')}
            <div className="flex gap-2 mt-5">
              <Button onClick={createTracker} disabled={saving} className="bg-primary text-primary-foreground" data-testid="submit-add-dot-batch">{saving ? 'Saving...' : 'Add Batch Tracker'}</Button>
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <LinkExistingDialog
        open={linkOpen}
        onOpenChange={setLinkOpen}
        title="Link Existing Batch Trackers"
        description="Select DOT batch trackers recorded on this property to also track under this project."
        records={linkCandidates}
        renderPrimary={(t) => t.label || 'Batch tracker'}
        renderSecondary={(t) => `Expires ${t.expiration_date || 'n/a'}${t.status ? ` · ${t.status}` : ''}`}
        linking={linking}
        onConfirm={linkSelected}
        emptyText="No unlinked batch trackers found for this property."
        testid="link-existing-dot-batch"
      />
    </div>
  );
}
